import React, { createContext, useCallback, useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import GwaPromoIneligibleModal from '../components/GwaPromoIneligibleModal';
import * as events from '../lib/analytics/events';
import { cartActions } from '../reducers/storeCart';

type DropGwaPromo = (couponCode: string | undefined, surface: string) => void;

export const GwaPromoNoticeContext = createContext<DropGwaPromo | undefined>(undefined);

interface GwaPromoNoticeProviderProps {
  children: React.ReactNode;
}

/**
 * Owns the notice shown when the adoption promo comes off a cart because the shopper's email has
 * already used it. It sits above the router because the answer can arrive on whichever page prices the
 * cart first, and the shopper has to be told wherever that is. The open state is deliberately not in
 * redux, which is persisted: a reload would otherwise reopen a notice about a promo that is long gone.
 */
export function GwaPromoNoticeProvider({ children }: GwaPromoNoticeProviderProps) {
  const dispatch = useDispatch();
  const [opened, setOpened] = useState(false);

  const dropGwaPromo = useCallback(
    (couponCode: string | undefined, surface: string) => {
      dispatch(cartActions.removeCoupon());
      events.cartPage.couponError(couponCode ?? '', 'gwa-already-redeemed', {
        cartEmpty: false,
        hadEntitlement: true,
        surface,
      });
      setOpened(true);
    },
    [dispatch],
  );

  const close = useCallback(() => setOpened(false), []);

  return (
    <GwaPromoNoticeContext.Provider value={dropGwaPromo}>
      {children}
      <GwaPromoIneligibleModal opened={opened} onClose={close} />
    </GwaPromoNoticeContext.Provider>
  );
}

/**
 * Takes the adoption promo off the cart and tells the shopper why. `surface` names where the rejection
 * was noticed, for the coupon error event.
 */
export function useDropAlreadyRedeemedGwaPromo(): DropGwaPromo {
  const dropGwaPromo = useContext(GwaPromoNoticeContext);
  if (!dropGwaPromo) {
    throw new Error('useDropAlreadyRedeemedGwaPromo must be used within a GwaPromoNoticeProvider');
  }

  return dropGwaPromo;
}

/**
 * For code that also runs where no notice is mounted, such as a story: there is nothing to open there,
 * so the promo simply stays on the cart and the caller carries on as it did before.
 */
export function useOptionalDropAlreadyRedeemedGwaPromo(): DropGwaPromo | undefined {
  return useContext(GwaPromoNoticeContext);
}
